import * as authentication from './authentication.js';

/**
 * Generate exception declaration based on type and form data
 * @param {string} exceptionType - Type of exception (independence-exception, limosa-exception, etc.)
 * @param {Object} formData - Form data containing all necessary information
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Generated exception document
 */
export const generateExceptionDeclaration = async (exceptionType, formData, options = {}) => {
	const {
		generateUrl = '/api/files/generate-declaration',
		isPublic = false,
		fileName = 'Independence Exception Request',
		addPendingAction = () => {},
		removePendingAction = () => {},
	} = options;

	const action = crypto.randomUUID();
	try {
		addPendingAction(action);

		// Send all data in the request body
		const requestBody = {
			type: exceptionType,
			fileName,
			...formData,
			generatedAt: new Date().toISOString(),
		};

		// Use fetch to send generation request with proper authentication
		const authParams = authentication.extractAuthParams();
		const authHeader = authentication.extractAuthorization();
		const uploadedForId = new URLSearchParams(window.location.search).get('userId') || '';
		
		const url = `${generateUrl}?${authParams}&isPublic=${isPublic}`;
		const headers = {
			'Content-Type': 'application/json',
		};
		
		if (authHeader) {
			headers['Authorization'] = authHeader;
		}
		
		if (uploadedForId) {
			headers['x-uploaded-for'] = uploadedForId;
		}

		const response = await fetch(url, {
			method: 'POST',
			headers,
			body: JSON.stringify(requestBody),
		});

		if (!response.ok) {
			// Provide user-friendly error messages based on HTTP status
			let errorMessage = 'Failed to generate exception request';
			
			if (response.status === 401) {
				errorMessage = 'Authentication failed.';
			} else if (response.status === 403) {
				errorMessage = 'You do not have permission to generate this exception request.';
			} else if (response.status === 404) {
				errorMessage = 'Exception generation service not found.';
			} else if (response.status >= 500) {
				errorMessage = 'Server error. Please try again later.';
			} else if (response.statusText) {
				errorMessage = `Failed to generate exception request: ${response.statusText}.`;
			}
			
			// Add support contact guidance for all errors
			errorMessage += ' Please contact support if this problem keeps occurring.';
			
			throw new Error(errorMessage);
		}

		const generatedException = await response.json();
		
		removePendingAction(action);
		return generatedException;
	} catch (error) {
		console.error('Exception Generator - Error occurred:', error);
		removePendingAction(action);
		
		// Provide user-friendly error messages for common network issues
		if (error.name === 'TypeError' && error.message.includes('fetch')) {
			throw new Error('Network error. Please check your connection and try again. Contact support if this problem keeps occurring.');
		} else if (error.name === 'AbortError') {
			throw new Error('Request was cancelled. Please try again. Contact support if this problem keeps occurring.');
		} else {
			// Use the original error message if it's already user-friendly, otherwise provide a generic one
			const baseMessage = error.message || 'Failed to generate exception request';
			// Add support guidance for all errors (avoid duplication)
			const messageWithSupport = baseMessage.includes('contact support') ? baseMessage : baseMessage + ' Please contact support if this problem keeps occurring.';
			throw new Error(messageWithSupport);
		}
	}
};

/**
 * Prepare form data for independence exception
 * @param {Object} values - Form values from the social security section
 * @returns {Object} - Prepared data for independence exception
 */
export const prepareIndependenceExceptionData = (values) => {
	// Find the selected group to get its name
	const selectedGroup = values.userData?.groups?.find(group => group.id === values.selectedGroup);
	const groupName = selectedGroup?.name || 'Unknown Group';

	const preparedData = {
		// Company data
		vatNumber: values.vatNumber,
		
		// Group information
		selectedGroupName: groupName,
		
		// Include all form fields dynamically (checkbox, signature, dates, etc.)
		...values,
	};

	return preparedData;
};

/**
 * Prepare form data for Limosa exception
 * @param {Object} values - Form values from the social security section
 * @returns {Object} - Prepared data for Limosa exception
 */
export const prepareLimosaExceptionData = (values) => {
	// Find the selected group to get its name
	const selectedGroup = values.userData?.groups?.find(group => group.id === values.selectedGroup);
	const groupName = selectedGroup?.name || 'Unknown Group';

	const preparedData = {
		// Company data
		vatNumber: values.vatNumber,
		
		// Group information
		selectedGroupName: groupName,
		
		// Include all form fields dynamically (signature, radio, dates, etc.)
		...values,
	};

	return preparedData;
};

/**
 * Get the appropriate data preparation function based on exception type
 * @param {string} exceptionType - Type of exception
 * @returns {Function} - Data preparation function
 */
export const getExceptionDataPreparer = (exceptionType) => {
	switch (exceptionType) {
	case 'independence-exception':
		return prepareIndependenceExceptionData;
	case 'limosa-exception':
		return prepareLimosaExceptionData;
	default:
		// For unknown types, return a generic preparer that includes all data
		return (values) => ({
			...values,
			exceptionType,
			generatedAt: new Date().toISOString(),
		});
	}
};

/**
 * Generate exception with automatic data preparation
 * @param {string} exceptionType - Type of exception
 * @param {Object} formValues - Raw form values
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Generated exception document
 */
export const generateExceptionWithDataPreparation = async (exceptionType, formValues, options = {}) => {
	const dataPreparer = getExceptionDataPreparer(exceptionType);
	const preparedData = dataPreparer(formValues);
	
	return await generateExceptionDeclaration(exceptionType, preparedData, options);
}; 